import { DEFAULT_PRESET_AMOUNT, type TextPreset } from './textAnim';
import type { ProceduralAnim } from '../types';

export type AnimSourceKind = 'text' | 'procedural' | 'sequence' | 'video' | 'scene' | 'morph';
export type MotionKind = 'static' | 'boiling' | 'scroll';
export type FilterKind = 'none' | 'ema' | 'median';

export interface AnimSettings {
  source: AnimSourceKind;
  preset: TextPreset;
  procedural: ProceduralAnim;
  /** Procedural animation over a flat plane or over the static source from "Create". */
  proceduralBase: 'flat' | 'studio';
  fps: number;
  duration: number;
  loop: 'loop' | 'ping_pong';
  width: number;
  height: number;
  motion: MotionKind;
  scrollSpeed: number;
  filter: FilterKind;
  emaAlpha: number;
  medianRadius: number;
  filterThreshold: number;
  /** Output format when rendering on the home server. */
  serverFormat: 'gif' | 'webm' | 'mp4' | 'zip';
}

export const PROCEDURAL_DEFAULTS: Record<ProceduralAnim['kind'], ProceduralAnim> = {
  waves: { kind: 'waves', amplitude: 0.5, wavelength: 0.3, direction: 0, cycles: 1 },
  noise: { kind: 'noise', amplitude: 0.7, scale: 120, cycles: 1, seed: 7 },
  ripple: { kind: 'ripple', amplitude: 0.6, cx: 0.5, cy: 0.5, wavelength: 0.18, cycles: 1, decay: 1.5 },
};

export function defaultAnimSettings(): AnimSettings {
  return {
    source: 'text',
    preset: { kind: 'swing', amount: DEFAULT_PRESET_AMOUNT.swing, cycles: 1 },
    procedural: { ...PROCEDURAL_DEFAULTS.ripple },
    proceduralBase: 'flat',
    fps: 20,
    duration: 3,
    loop: 'loop',
    width: 960,
    height: 540,
    motion: 'static',
    scrollSpeed: 1,
    filter: 'ema',
    emaAlpha: 0.5,
    medianRadius: 2,
    filterThreshold: 0.15,
    serverFormat: 'mp4',
  };
}

const KEY = 'stereo.anim.v1';

function load(): AnimSettings {
  const d = defaultAnimSettings();
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...d, ...(JSON.parse(raw) as Partial<AnimSettings>) } : d;
  } catch {
    return d;
  }
}

export const anim = $state<AnimSettings>(load());

let timer: ReturnType<typeof setTimeout> | undefined;
export function persistAnim() {
  clearTimeout(timer);
  timer = setTimeout(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify($state.snapshot(anim)));
    } catch {
      /* ignore */
    }
  }, 300);
}
